<template>
  <div class="context-editor">
    <div class="context-editor__top">
      <div class="context-editor__title-row">
        <span class="context-editor__title">{{ title }}</span>
        <div v-if="showActions" class="context-editor__actions">
          <StandardButton
            v-if="isDirty"
            label="Save Changes"
            size="small"
            :disabled="hasMaxError"
            @click="handleSave"
          />
          <StandardButton
            v-if="isDirty"
            label="Discard"
            variant="outline"
            size="small"
            @click="handleDiscard"
          />
          <AskIQButton
            variant="light"
            :label="iqActionLabel"
            :disabled="disabled || suggesting"
            :aria-busy="suggesting ? 'true' : 'false'"
            :title="suggestHint || undefined"
            @click="handleIqAction"
          />
        </div>
      </div>

      <div class="context-editor__meta-row">
        <p class="context-editor__subtitle">
          IQ uses this context to reason and formulate better answers.
        </p>
        <div class="context-editor__counter-row">
          <span v-if="hasMaxError" :id="reduceHintId" class="context-editor__reduce-hint">
            Reduce by {{ overByFormatted }}
          </span>
          <span v-if="hasMaxError" class="context-editor__counter context-editor__counter--error">
            <span class="character-counter-error">{{ charCountFormatted }}</span>
            <span class="character-counter-limit">{{ counterLimitLabel }}</span>
          </span>
          <span v-else class="character-counter">
            {{ charCountFormatted }} / {{ maxLengthFormatted }}
          </span>
        </div>
      </div>
      <p v-if="suggestError" class="context-editor__suggest-error" role="alert">
        {{ suggestError }}
      </p>
    </div>

    <div
      class="context-editor__textarea-wrapper"
      :class="{
        'context-editor__textarea-wrapper--dirty': isDirty && !hasMaxError,
        'context-editor__textarea-wrapper--error': hasMaxError,
      }"
    >
      <textarea
        ref="textareaRef"
        :id="textareaId"
        class="context-editor__textarea"
        :value="localValue"
        :placeholder="placeholder"
        :maxlength="maxLength > 0 ? maxLength : undefined"
        :disabled="disabled || suggesting"
        :aria-describedby="hasMaxError ? reduceHintId : undefined"
        :aria-label="title"
        @input="handleInput"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import AskIQButton from "@components/AskIQButton/AskIQButton.vue";

const props = defineProps({
  // ER-10219: caller-supplied heading (e.g. "Account Context" in settings).
  title: {
    type: String,
    default: "Context",
  },
  context: {
    type: String,
    default: "",
  },
  // Optional unsaved draft; when it differs from `context`, the editor mounts in a dirty state.
  draft: {
    type: String,
    default: null,
  },
  maxLength: {
    type: Number,
    default: 10000,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // ER-10218: an IQ suggestion request is in flight — shows "Suggesting…" and locks the field.
  suggesting: {
    type: Boolean,
    default: false,
  },
  // ER-10218: inline message shown when the last suggestion request failed.
  suggestError: {
    type: String,
    default: "",
  },
  // ER-10219: optional hover hint on the IQ action (e.g. "no company websites
  // yet, click to paste URLs"). Empty means no tooltip.
  suggestHint: {
    type: String,
    default: "",
  },
  // Hide the Save/Discard/AskIQ action row (e.g. when embedded in a save-on-submit dialog).
  showActions: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["save", "discard", "suggest", "improve", "input"]);

const textareaId = `context-editor-textarea-${Math.random().toString(36).slice(2, 9)}`;
const reduceHintId = `context-editor-reduce-${Math.random().toString(36).slice(2, 9)}`;

const localValue = ref(resolveLocalValue(props.draft, props.context));
// Baseline for dirty detection. Updated on save so buttons/highlight clear immediately,
// and when props.context catches up after async load/refresh.
const originalValue = ref(props.context || "");
const textareaRef = ref(null);

const placeholder =
  "e.g. Employees are expected to proactively participate in cross-functional initiatives — contributing ideas, joining working groups, and following through on commitments…";

const charCount = computed(() => (localValue.value ? localValue.value.length : 0));
const hasMaxError = computed(() => props.maxLength > 0 && charCount.value > props.maxLength);
const overBy = computed(() => Math.max(0, charCount.value - props.maxLength));
const charCountFormatted = computed(() => charCount.value.toLocaleString("en-US"));
const maxLengthFormatted = computed(() => props.maxLength.toLocaleString("en-US"));
const overByFormatted = computed(() => overBy.value.toLocaleString("en-US"));
const counterLimitLabel = computed(() => `/ ${maxLengthFormatted.value}`);
const isDirty = computed(() => localValue.value !== originalValue.value);
const iqActionLabel = computed(() => {
  if (props.suggesting) return "Suggesting…";
  return charCount.value > 0 ? "Improve" : "Suggest with IQ";
});

function resolveLocalValue(draft, context) {
  if (draft !== null && draft !== undefined) {
    return draft;
  }
  return context || "";
}

function resizeTextarea() {
  const textarea = textareaRef.value;
  if (!textarea) return;
  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
}

watch([() => props.context, () => props.draft], ([newContext, newDraft], [oldContext] = []) => {
  const committed = newContext || "";
  const previousCommitted = oldContext || "";

  if (newDraft !== null && newDraft !== undefined) {
    localValue.value = newDraft;
    originalValue.value = committed;
    nextTick(resizeTextarea);
    return;
  }

  // Sync when clean, when parent matches the draft (post-save), or when the textarea
  // still shows the previous committed value (async context arriving after empty mount).
  // Without the last case, first open stays blank: empty local looks "dirty" vs new context
  // and the watch would refuse to overwrite.
  if (
    !isDirty.value ||
    committed === localValue.value ||
    localValue.value === previousCommitted
  ) {
    localValue.value = committed;
    originalValue.value = committed;
    nextTick(resizeTextarea);
  }
});

watch(localValue, () => nextTick(resizeTextarea));

onMounted(() => nextTick(resizeTextarea));

function handleInput(e) {
  let value = e.target.value;
  // maxlength does not stop programmatic/autofill sets, so clamp growth here. Shrinking an
  // over-limit suggestion is left alone so the user can trim it down.
  if (props.maxLength > 0 && value.length > props.maxLength && value.length > localValue.value.length) {
    value = value.slice(0, props.maxLength);
  }
  // Re-sync the DOM: when the clamped value equals the old one Vue sees no change to patch.
  e.target.value = value;
  localValue.value = value;
  emit("input", localValue.value);
  resizeTextarea();
}

function handleSave() {
  if (hasMaxError.value) return;
  originalValue.value = localValue.value;
  emit("save", localValue.value || null);
}

function handleDiscard() {
  localValue.value = originalValue.value;
  emit("discard");
  nextTick(resizeTextarea);
}

function handleIqAction() {
  if (charCount.value > 0) {
    emit("improve");
  } else {
    emit("suggest");
  }
}
</script>

<style scoped>
.context-editor {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.context-editor__top {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.context-editor__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
}

.context-editor__meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
}

.context-editor__title {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  color: #101828;
  flex-shrink: 0;
}

.context-editor__subtitle {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #475467;
  margin: 0;
  min-width: 0;
  flex: 1;
}

.context-editor__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex-shrink: 0;
}

/* AskIQButton Figma secondary overrides — targets .ask-iq-button, .ask-label, .ask-iq-icon, .ask-hover-object, .v-btn--disabled from AskIQButton.vue; !important overrides Vuetify v-btn theme + AskIQButton scoped styles */
.context-editor__actions :deep(.ask-iq-button) {
  min-width: auto !important; /* overrides .ask-iq-button min-width: 200px */
  justify-content: flex-start !important; /* overrides Vuetify .justify-space-between utility on v-btn */
  background-color: #f2edff !important; /* overrides .ask-light-button background-color: #7F56D9 */
  border: 1px solid #7f56d9 !important; /* Figma secondary border — .ask-light-button sets no border */
  box-shadow: 0 1px 2px 0 #1018280d; /* Figma secondary shadow — overrides .ask-iq-button box-shadow: none */
  transition: background-color 0.2s ease !important;
}

.context-editor__actions :deep(.ask-iq-button:hover),
.context-editor__actions :deep(.ask-iq-button.ask-hover-object) {
  background-color: #e4dbfc !important; /* overrides .ask-hover-object.ask-light-button background-color: #5B2CC5 */
}

.context-editor__actions :deep(.ask-iq-button .ask-label) {
  color: #7f56d9 !important; /* overrides .ask-label color: #FFFFFF (solid default) */
}

.context-editor__actions :deep(.ask-iq-button > svg path) {
  fill: #7f56d9 !important; /* spark icon — overrides :fill="#FFFFFF" on solid variant */
}

.context-editor__actions :deep(.ask-iq-icon svg rect) {
  fill: #ed2738 !important; /* IQ badge background — Figma #ED2738 */
}

.context-editor__actions :deep(.ask-iq-icon svg path) {
  fill: #ffffff !important; /* IQ badge icon paths */
}

.context-editor__actions :deep(.ask-iq-button.v-btn--disabled) {
  background-color: #e4e7ec !important; /* overrides .ask-iq-button.v-btn--disabled background-color: #E4E7EC */
  border-color: #e4e7ec !important; /* .ask-iq-button.v-btn--disabled sets no border-color */
}

.context-editor__actions :deep(.ask-iq-button.v-btn--disabled .ask-label) {
  color: #98a2b3 !important; /* overrides .ask-iq-button.v-btn--disabled .ask-label color: #98A2B3 */
}

.context-editor__suggest-error {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  color: #d92d20;
}

.context-editor__counter-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  flex-shrink: 0;
}

.context-editor__reduce-hint {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  color: #d92d20;
  white-space: nowrap;
}

.context-editor__counter {
  white-space: nowrap;
}

.context-editor__counter--error {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.character-counter {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #667085;
  white-space: nowrap;
}

.character-counter-error {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  color: #d92d20;
}

.character-counter-limit {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #667085;
}

.context-editor__textarea-wrapper {
  width: 100%;
  min-height: 150px;
  box-sizing: border-box;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background-color: #ffffff;
  padding: 12px 14px;
}

.context-editor__textarea-wrapper--dirty {
  border: 2px solid #7f56d9;
}

.context-editor__textarea-wrapper--error {
  /* !important needed to override the --dirty border when both classes apply during an over-limit edit */
  border:2px solid #F04438 !important;
}

.context-editor__textarea {
  display: block;
  width: 100%;
  min-height: 126px;
  box-sizing: border-box;
  padding: 0;
  border: none;
  outline: none;
  resize: none;
  overflow: hidden;
  field-sizing: content;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #101828;
  background: transparent;
}

.context-editor__textarea::placeholder {
  color: #667085;
}

.context-editor__textarea:disabled {
  color: #98a2b3;
  cursor: not-allowed;
}

/* StandardButton disabled override — targets .standard-button--magenta.standard-button--primary.standard-button--disabled from StandardButton */
.context-editor__actions
  :deep(.standard-button--magenta.standard-button--primary.standard-button--disabled) {
  background-color: #eaecf0;
  border-color: #eaecf0;
  color: #98a2b3;
}
</style>
